// @ts-nocheck
import JoTooltip from '@jo-v1/features/JoTooltip';
import { useState } from 'react';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core/shared/components/Tabs';
import cn from '@core/utils/classNames';
import { highLightKeywords, paragraphToSentencesArray } from '@core/utils/strings/programDescriptions';

import decisionFactorsFlagsIcons from '../DecisionFactorsFlags/DecisionFactorsFlagsDictionary';
import KEYWORDS from '../RecommenderCard/keywords.json';
import styles from './SchoolProgramTabs.module.css';

interface ProgramPanelProps {
  programSnippet: string;
}

export const ProgramPanel = ({ programSnippet }: ProgramPanelProps) => {
  const programSnippetWithHighlightedKeywords = highLightKeywords(programSnippet, KEYWORDS);
  const itemsList = paragraphToSentencesArray(programSnippetWithHighlightedKeywords);
  return (
    <ul id="programTabPanel" className={styles.programInfoList}>
      {itemsList.map((bullet, idx) => (
        <li key={idx} dangerouslySetInnerHTML={{ __html: bullet }} />
      ))}
    </ul>
  );
};

interface SchoolAdditionalInfo {
  levelOfInstitution?: string;
  accreditator?: string;
  roiPercentileRank?: number;
  percentOnlineEnrollment?: number;
}

export const SchoolPanel = ({ schoolAdditionalInfo }: { schoolAdditionalInfo: SchoolAdditionalInfo }) => {
  let roiLevel = 'Good';
  const { roiPercentileRank = 0 } = schoolAdditionalInfo || {};
  if (roiPercentileRank >= 0.8) {
    roiLevel = 'Best';
  } else if (roiPercentileRank >= 0.5) {
    roiLevel = 'Better';
  }

  return (
    <ul className={styles.schoolInfoList}>
      {schoolAdditionalInfo?.accreditator && (
        <li>
          <span>
            {decisionFactorsFlagsIcons.accredit}
            <div>
              Accreditation
              <JoTooltip
                text={
                  <>
                    <h4>Accreditation</h4>
                    {schoolAdditionalInfo?.accreditator}
                  </>
                }
              />
            </div>
          </span>{' '}
          <strong>{schoolAdditionalInfo.accreditator !== '' ? 'Yes' : 'No'}</strong>
        </li>
      )}
      {schoolAdditionalInfo?.percentOnlineEnrollment && (
        <li>
          <span>
            {decisionFactorsFlagsIcons.online}
            <div>
              Online Enrollment
              <JoTooltip
                text={
                  <>
                    <h4>Online Enrollment</h4>
                    Percentage of all students who are enrolled online.
                  </>
                }
              />
            </div>
          </span>{' '}
          <strong>{Math.round(Number(schoolAdditionalInfo.percentOnlineEnrollment))}%</strong>
        </li>
      )}
    </ul>
  );
};

interface SchoolProgramTabsProps {
  programSnippet: string;
  schoolAdditionalInfo: SchoolAdditionalInfo;
  layout?: 'column' | 'row' | 'row-reverse' | 'column-reverse';
}

const SchoolProgramTabs = ({ programSnippet, schoolAdditionalInfo, layout = 'column' }: SchoolProgramTabsProps) => {
  const [activeTab, setActiveTab] = useState('program');
  interface HandleTabClickProps {
    (activeTab: string): void;
  }

  const whyThisSchoolClickPayload = {
    webElement: {
      elementType: 'toggle',
      location: 'program-card',
      name: 'program-blurb',
      htmlId: 'toggle-program-blurb',
      text: 'Why This Program',
    },
  };

  const schoolProgramPayload = {
    webElement: {
      elementType: 'toggle',
      location: 'program-card',
      name: 'school-info',
      htmlId: 'toggle-school-info',
      text: 'School Info',
    },
  };

  const { elementClicked } = useElementEvents();

  const handleTabClick: HandleTabClickProps = (activeTab) => {
    setActiveTab(activeTab);
    elementClicked(activeTab === 'program' ? whyThisSchoolClickPayload : schoolProgramPayload);
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabClick}
      className={cn(styles.tabsContainer, styles[`layout-${layout}`])}
    >
      <TabsList className={styles.tabsList}>
        <TabsTrigger
          className={cn(styles.tabTrigger, activeTab === 'program' && styles.active)}
          value="program"
          onClick={(e) => e.stopPropagation()}
        >
          Why Consider This Program
        </TabsTrigger>
        <TabsTrigger
          className={cn(styles.tabTrigger, activeTab === 'school' && styles.active)}
          value="school"
          onClick={(e) => e.stopPropagation()}
        >
          School Info
        </TabsTrigger>
      </TabsList>

      <div
        id="school-program-tabs"
        className={styles.tabsContentContainer}
        style={{ height: typeof window !== 'undefined' ? undefined : 'auto' }}
        ref={(el) => {
          if (el) {
            const ul = el.querySelector('#programTabPanel');
            if (ul) {
              el.style.height = `${(ul as HTMLElement).offsetHeight}px`;
            }
          }
        }}
      >
        <TabsContent className={styles.tabContent} value="program">
          <ProgramPanel programSnippet={programSnippet} />
        </TabsContent>
        <TabsContent className={styles.tabContent} value="school">
          <SchoolPanel schoolAdditionalInfo={schoolAdditionalInfo} />
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default SchoolProgramTabs;
